<template>
  <div>
    <Loader
      v-if="loading"
      label="Данные загружаются"
      :active="loading"
      height="300"
    />
    <div v-else>
      <div
        v-if="positionSwitcherItems.length"
        class="position-switcher"
      >
        <Switcher
          v-model="positionSwitcherValue"
          class="h-switcher"
          type="group"
          :items="positionSwitcherItems"
        >
          <template #label="{ item }">
            <div class="position-switcher__item">
              <div class="position-switcher__label">
                {{ item.name }}
              </div>
              <Button
                v-if="item.hasExpired"
                v-b-popover.html="{
                  content: 'Категория содержит позиции, которые просрочены',
                  trigger: 'hover blur',
                  placement: 'top',
                }"
                class="position-switcher__icon"
                icon="time-is-out"
                size="iconic"
              />
              <Button
                v-if="item.hasUnissued"
                v-b-popover.html="{
                  content: 'Категория содержит позиции, которые не выданы',
                  trigger: 'hover blur',
                  placement: 'top',
                }"
                class="position-switcher__icon"
                icon="need"
                size="iconic"
              />
            </div>
          </template>
        </Switcher>
      </div>
      <template v-if="hasItems">
        <div
          v-if="hasSelectedPosition"
          class="desktop"
        >
          <ScrolledTable
            v-if="selectedPosition.hasItems"
            :fix-last="true"
          >
            <table class="h-table inventory-items inventory-items--desktop">
              <thead>
                <tr class="inventory-item">
                  <th class="inventory-item__name">Серийный номер / Наименование ТМЦ</th>
                  <th class="inventory-item__model">Модель типа / Модель ТМЦ</th>
                  <th class="inventory-item__status">Статус</th>
                  <th class="inventory-item__issued-at">Дата выдачи</th>
                  <th class="inventory-item__issuer">Выдал</th>
                  <th class="inventory-item__will-expire-at">Срок годности</th>
                  <th class="inventory-item__responsible">Ответственный</th>
                  <th class="inventory-item__actions"></th>
                </tr>
              </thead>
              <template v-for="category in selectedPosition.categories">
                <tr
                  :key="`cat_${category.key}`"
                  class="inventory-position h-table__row--header"
                  :class="{
                    'collapsed': !category.isExpanded,
                  }"
                  @click="category.isExpanded = !category.isExpanded"
                >
                  <td colspan="8">
                    <div class="d-flex align-items-center">
                      <svg-icon
                        class="position-toggler"
                        name="chevron-top-circled"
                        color="primary"
                        :size="24"
                      />
                      <span class="inventory-position__name">
                        {{ category.name }}
                      </span>
                      <Button
                        v-if="category.hasExpired"
                        v-b-popover.html="{
                          id: 'inventory-position-expired',
                          container: '#app',
                          trigger: 'hover blur',
                          content: 'Категория содержит позиции, которые просрочены',
                          placement: 'top',
                        }"
                        class="inventory-position__icon"
                        icon="time-is-out"
                        size="iconic"
                      />
                      <Button
                        v-if="category.hasUnissued"
                        v-b-popover.html="{
                          id: 'inventory-position-not-issued',
                          container: '#app',
                          trigger: 'hover blur',
                          content: 'Категория содержит позиции, которые не выданы',
                          placement: 'top',
                        }"
                        class="inventory-position__icon"
                        icon="need"
                        size="iconic"
                      />
                    </div>
                  </td>
                </tr>
                <b-collapse
                  :key="`cat_${category.key}_exp`"
                  v-model="category.isExpanded"
                  tag="tbody"
                >
                  <EntryRow
                    v-for="entry in category.entries"
                    :key="`ent_${entry.key}`"
                    :entry="entry"
                  >
                    <EntryControls
                      :entry="entry"
                      @issue="issueItem"
                      @withdraw="withdrawItem"
                      @discard="discardItem"
                      @transfer="transferItem"
                      @showInfo="showItemInfo"
                      @showHistory="showItemHistory"
                    />
                  </EntryRow>
                  <template v-for="subcategory in category.subcategories">
                    <tr
                      :key="`subcat_${subcategory.key}_title`"
                      class="inventory-group"
                    >
                      <td colspan="8">
                        {{ subcategory.name }}
                      </td>
                    </tr>
                    <EntryRow
                      v-for="entry in subcategory.entries"
                      :key="`ent_sub_${entry.key}`"
                      :entry="entry"
                    >
                      <EntryControls
                        :entry="entry"
                        @issue="issueItem"
                        @withdraw="withdrawItem"
                        @discard="discardItem"
                        @transfer="transferItem"
                        @showInfo="showItemInfo"
                        @showHistory="showItemHistory"
                      />
                    </EntryRow>
                  </template>
                </b-collapse>
              </template>
            </table>
          </ScrolledTable>
          <div
            v-else
            class="empty"
          >
            Для этой должности не указаны типы ТМЦ
          </div>
        </div>
        <div
          v-if="hasSelectedPosition"
          class="mobile"
        >
          <div class="inventory-items__container">
            <div
              v-for="category in mobileSelectedPosition.categories"
              :key="`cat_${category.key}`"
              class="inventory-position"
            >
              <div
                v-b-toggle="'position-collapse-m-' + category.key"
                class="inventory-position-header d-flex align-items-center"
              >
                <svg-icon
                  class="position-toggler"
                  name="chevron-top-circled"
                  color="primary"
                  :size="24"
                />
                <span class="inventory-position__name">
                  {{ category.name }}
                </span>
                <Button
                  v-if="category.hasExpired"
                  v-b-popover.html="{
                    content: 'Категория содержит позиции, которые просрочены',
                    trigger: 'hover blur',
                    placement: 'top',
                  }"
                  class="inventory-position__icon"
                  icon="time-is-out"
                  size="iconic"
                />
                <Button
                  v-if="category.hasUnissued"
                  v-b-popover.html="{
                    content: 'Категория содержит позиции, которые не выданы',
                    trigger: 'hover blur',
                    placement: 'top',
                  }"
                  class="inventory-position__icon"
                  icon="need"
                  size="iconic"
                />
              </div>
              <b-collapse
                :id="'position-collapse-m-' + category.key"
                tag="div"
                visible
                class="inventory-position__table"
              >
                <HorizontalTable>
                  <table class="h-table h-table--horizontal inventory-items inventory-items--mobile">
                    <tr>
                      <th>Подкатегория ТМЦ</th>
                      <td
                        v-for="(subcategory, subcategoryIndex) in category.data.subcategory"
                        :key="subcategoryIndex"
                      >
                        {{ subcategory || '—' }}
                      </td>
                    </tr>
                    <tr>
                      <th>Серийный номер / Наименование ТМЦ </th>
                      <td
                        v-for="(name, nameIndex) in category.data.name"
                        :key="nameIndex"
                      >
                        <div
                          :class="{
                            'greyed-out': !name.isIssued,
                          }"
                        >
                          {{ name.serialNumber || '—' }} / {{ name.item || '—' }}
                        </div>
                        <div class="inventory-position__type">
                          {{ name.type }}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>Модель типа / Модель ТМЦ</th>
                      <td
                        v-for="(model, modelIndex) in category.data.model"
                        :key="modelIndex"
                      >
                        {{ model.type || '—' }} /
                        <span
                          :class="{
                            'greyed-out': !model.isIssued,
                          }"
                        >
                          {{ model.item || '—' }}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <th>Статус</th>
                      <td
                        v-for="(status, statusIndex) in category.data.status"
                        :key="statusIndex"
                        :class="{
                          'h-table__cell--warning': !status.isIssued,
                          'h-table__cell--error': status.isExpired,
                        }"
                      >
                        {{ status.name || 'Не выдан' }}
                      </td>
                    </tr>
                    <tr>
                      <th>Дата выдачи</th>
                      <td
                        v-for="(issuedAt, issuedAtIndex) in category.data.issuedAt"
                        :key="issuedAtIndex"
                      >
                        {{ issuedAt }}
                      </td>
                    </tr>
                    <tr>
                      <th>Выдал</th>
                      <td
                        v-for="(issuer, issuerIndex) in category.data.issuer"
                        :key="issuerIndex"
                      >
                        <template v-if="issuer">
                          <a
                            v-if="issuer.id"
                            :href="`/index.php/?r=redesign#/personal-employee/${issuer.id}/summary`"
                            target="_blank"
                          >
                            {{ issuer.name }}
                          </a>
                          <template v-else>
                            {{ issuer.name}}
                          </template>
                        </template>
                      </td>
                    </tr>
                    <tr>
                      <th>Срок годности</th>
                      <td
                        v-for="(willExpireAt, willExpireAtIndex) in category.data.willExpireAt"
                        :key="willExpireAtIndex"
                      >
                        {{ willExpireAt || '—' }}
                      </td>
                    </tr>
                    <tr>
                      <th>Ответственный</th>
                      <td
                        v-for="(responsible, responsibleIndex) in category.data.responsible"
                        :key="responsibleIndex"
                      >
                        <a
                          v-if="responsible"
                          :href="`?r=redesign#/personal-employee/${responsible.id}/summary`"
                          target="_blank"
                        >
                          {{ responsible.name }}
                        </a>
                        <template v-else>
                          —
                        </template>
                      </td>
                    </tr>
                    <tr>
                      <th></th>
                      <td
                        v-for="(entry, entryIndex) in category.data.entry"
                        :key="entryIndex"
                      >
                        <EntryControls
                          :entry="entry"
                          @issue="issueItem"
                          @withdraw="withdrawItem"
                          @discard="discardItem"
                          @transfer="transferItem"
                          @showInfo="showItemInfo"
                          @showHistory="showItemHistory"
                        />
                      </td>
                    </tr>
                  </table>
                </HorizontalTable>
              </b-collapse>
            </div>
          </div>
          <div v-if="!positions.length" class="inventory-items__empty">Записи не найдены</div>
        </div>
      </template>
      <div
        v-else
        class="empty"
      >
        Для этой должности не указаны типы ТМЦ
      </div>
    </div>
    <IssueItemModal
      ref="issueItemModal"
      :worker-id="workerId"
      @submit="$emit('change')"
    />
    <WithdrawItemModal
      ref="withdrawItemModal"
      :worker-id="workerId"
      @submit="$emit('change')"
    />
    <DiscardItemModal
      ref="discardItemModal"
      :worker-id="workerId"
      @submit="$emit('change')"
    />
    <TransferItemModal
      ref="transferItemModal"
      :worker-id="workerId"
      @submit="$emit('change')"
    />
    <HistorySidesheet ref="historySidesheet" />
    <InfoSidesheet ref="infoSidesheet" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import Loader from "@/components/loader.vue";
import Switcher from "@/components/controls2/Switcher.vue";
import Button from "@/components/controls2/Button.vue";
import SvgIcon from "@/components/svg-icon.vue";
import HorizontalTable from "@/components/horizontal-table.vue";
import ScrolledTable from "@/components/scrolled-table.vue";
import InfoSidesheet from "@/components/modals/inventory/ItemInfoSidesheet.vue";
import HistorySidesheet from "@/components/modals/inventory/ItemHistorySidesheet.vue";

import { Inventory } from "../../models";

import EntryRow from "./EntryRow.vue";
import EntryControls from "./EntryControls.vue";
import IssueItemModal from "../IssueItemModal.vue";
import WithdrawItemModal from "../WithdrawItemModal.vue";
import DiscardItemModal from "../DiscardItemModal.vue";
import TransferItemModal from "../TransferItemModal.vue";

export default {
  name: "items-table",
  components: {
    Loader,
    Switcher,
    Button,
    SvgIcon,
    HorizontalTable,
    ScrolledTable,
    EntryRow,
    EntryControls,
    InfoSidesheet,
    HistorySidesheet,
    IssueItemModal,
    WithdrawItemModal,
    DiscardItemModal,
    TransferItemModal,
  },
  props: {
    workerId: {
      type: [String, Number],
      required: true,
    },
  },
  data: () => ({
    loading: true,
    positions: [],
    hasItems: false,
    showActions: false,
    positionSwitcherValue: null,
  }),
  computed: {
    ...mapGetters(["getUserPermissions"]),
    positionSwitcherItems() {
      const { positions } = this;

      if (positions.length > 1) {
        return positions.map(({ id, name, hasUnissued, hasExpired }) => ({
          value: id,
          name,
          hasUnissued,
          hasExpired,
        }));
      }

      return [];
    },
    selectedPosition() {
      return this.positions.find(({ id }) => this.positionSwitcherValue === id);
    },
    hasSelectedPosition() {
      return !!this.selectedPosition;
    },
    mobileSelectedPosition() {
      if (!this.hasSelectedPosition) {
        return null;
      }

      const position = this.selectedPosition;

      const result = {
        id: position.id,
        name: position.name,
        categories: [],
      };

      const addEntry = (to, entry, subcategoryName = null) => {
        to.data.subcategory.push(subcategoryName);
            
        to.data.name.push({
          serialNumber: entry.item.serialNumber,
          name: entry.item.name,
          type: entry.type.name,
          isIssued: entry.isIssued,
        });

        to.data.model.push({
          item: entry.item.model,
          type: entry.type.model,
          isIssued: entry.isIssued,
        });

        to.data.status.push({
          name: entry.item.status,
          isIssued: entry.isIssued,
          isExpired: entry.isExpired,
        });

        to.data.issuedAt.push(entry.item.issuedAt);
        to.data.issuer.push(entry.item.issuer);
        to.data.willExpireAt.push(entry.isIssued ? entry.item.willExpireAt : " ");
        to.data.responsible.push(entry.type.responsible);

        to.data.entry.push(entry);
      };

      position.categories.forEach((category) => {
        const resultCategory = {
          name: category.name,
          hasExpired: category.hasExpired,
          hasUnissued: category.hasUnissued,
          key: category.key,
          data: {
            subcategory: [],
            name: [],
            model: [],
            status: [],
            issuedAt: [],
            issuer: [],
            willExpireAt: [],
            responsible: [],
            isIssued: [],
            isExpired: [],
            entry: [],
          },
        };

        category.entries.forEach(entry => addEntry(resultCategory, entry));

        category.subcategories.forEach((subcategory) => {
          subcategory.entries.forEach(entry => addEntry(resultCategory, entry, subcategory.name));
        });

        result.categories.push(resultCategory);
      });

      return result;
    },
  },
  created() {
    this.fetchData();
  },
  methods: {
    userHavePermissionActionsInventoryItems() {
      return this.getUserPermissions.includes("updateWorkerUniform");
    },
    async fetchData() {
      this.loading = true;

      try {
        const response = await this.$store.dispatch(
          "personalEmployee/getWorkerInventoryItemsList",
          this.workerId
        );
        
        const { positions, hasItems } = new Inventory(Object.values(response));

        if (!this.positionSwitcherValue && positions.length > 0) {
          this.positionSwitcherValue = positions[0].id;
        }

        this.positions = positions;
        this.hasItems = hasItems;
      } catch (error) {
        console.error(error);
      }

      this.loading = false;
    },
    resize() {
      setTimeout(() => {
        document.querySelector(".layout").scroll(0, 1);
      }, 0);
    },
    issueItem({ type }) {
      this.$refs.issueItemModal.open(type);
    },
    withdrawItem({ item }) {
      this.$refs.withdrawItemModal.open(item.id);
    },
    discardItem({ item }) {
      this.$refs.discardItemModal.open(item.id);
    },
    transferItem({ item }) {
      this.$refs.transferItemModal.open(item.id);
    },
    showItemHistory({ item }) {
      this.$refs.historySidesheet.open(item);
    },
    showItemInfo({ type, item }) {
      this.$refs.infoSidesheet.open(item ? item.id : null, type.id);
    },
  },
};
</script>
<style lang="scss" scoped>

@import '~@/assets/styles/vars';

.inventory-items {
  tbody {
    td {
      transition: all 0.2s;
    }
  }

  tbody.collapse:not(.show) {
    ::v-deep button {
      display: none;
    }

    ::v-deep * {
      font-size: 0 !important;
      padding: 0 !important;
      margin: 0 !important;
      border: 0 !important;
    }
  }

  tbody.collapsing {
    display: table-row-group !important;
  }

  &__empty {
    border: 1px solid #dfe4ef;
    text-align: center;
    font-size: 13px;
    color: $color-label;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: -14px;
    position: relative;
    z-index: 2;
    background: white;
  }

  .desktop {
    .inventory-position {
      font-size: 13px;
      font-weight: bold;
      cursor: pointer;

      &__name {
        margin: 0 10px;
      }

      &__icon {
        margin-right: 2px;
      }
    }
  }

  .inventory-item {
    &__name {}

    &__model {
      width: 175px;
    }

    &__status {
      width: 90px;
    }

    &__issued-at,
    &__will-expire-at {
      width: 80px;
    }

    &__issuer,
    &__responsible {
      width: 160px;
    }

    &__actions {
      width: 68px;
    }
  }

  .mobile {
    display: none;
    margin-left: -20px;
    margin-right: -20px;

    .inventory-position-header {
      font-size: 13px;
      font-weight: bold;
      margin-top: -1px;
      margin-bottom: -1px;

      .inventory-position__name {
        margin: 0 10px;
      }
      .inventory-position__icon {
        margin-right: 8px;
      }
    }

    .inventory-position__type {
      font-size: 11px;
      margin-top: 5px;
      line-height: 110%;
      color: $label;
    }

    th {
      width: 135px;
      min-width: 135px;
      max-width: 135px;
    }

    td {
      min-width: 190px;
    }

    .inventory-position-header {
      background: #f5f7fa;
      border: 1px solid #dfe4ef;
      border-left: 0;
      // border-right: 0;
      padding: 8px 20px;
    }

    .inventory-position__table {
      margin-left: -2px;
      margin-right: -2px;
    }
  }

  @include onlyMobile {
    .mobile {
      display: block;
    }
    .desktop {
      display: none;
    }
  }

  &--desktop {
    table-layout: fixed;

    td {
      vertical-align: middle;
    }

    .inventory-group {
      color: #8e9cbb;
      font-weight: 500;
    }
  }

  .position-toggler {
    transform: rotate(180deg);
    transition: transform 250ms ease-in-out;
    cursor: pointer;
  }

  .collapsed .position-toggler {
    transform: rotate(0deg);
  }

  .position-switcher {
    margin-bottom: 25px;

    &__item {
      display: flex;
      align-items: center;
    }

    &__label {
      margin-right: 5px;
    }

    &__icon {
      margin-left: 2px;
    }
  }
}

.empty {
  color: $label;
  font-size: 13px;
  line-height: 110%;
}

.greyed-out {
  color: $label;
}
</style>
